import React, { useEffect, useRef } from "react";
import { bool } from "prop-types";
import { connect, useDispatch, useSelector } from "react-redux";
import Head from "pages/_components/Head";
import MainContainer from "pages/_components/MainContainer";
import { Field, Form, Formik } from "formik";
import Button from "pages/_components/Button";
import { actions as onboardingActions, selectors as onboardingSelectors } from "reducers/onboarding";

import * as i18nUtils from "util/i18n";
import * as configUtils from "util/config";
import { goBack, push } from "connected-react-router";
import gaEventTracker, { events } from "util/gaEventTracker";
import * as Yup from "yup";
import { ResponsiveHeader } from "pages/_components/header";
import I18n from "pages/_components/I18n";
import CustomText from "pages/_components/CustomText/CustomText";
import Credential from "pages/_components/fields/credentials/Credential";
import ToDoList from "../_components/ToDoList";
import Stepper from "../_components/Stepper";

const PAGE_ID = "StepPassword";
const FORM_ID = "onboarding.step.password";

const Mustache = require("mustache");

const StepPassword = ({ isMobile, onboardingWithInvitation }) => {
    const dispatch = useDispatch();
    const formRef = useRef(null);
    const { errorMessage, password: _password, passwordConfirmation } = useSelector((state) => state.onboarding);
    const minLength = configUtils.getInteger("core.password.minLength");
    const minLengthValidationMsg = Mustache.render(i18nUtils.get("onboarding.password.validation.minLength"), {
        minLength,
    });
    const maxLength = configUtils.getInteger("core.password.maxLength");
    const maxLengthValidationMsg = Mustache.render(i18nUtils.get("onboarding.password.validation.maxLength"), {
        maxLength,
    });

    useEffect(() => {
        if (!formRef.current && !errorMessage) {
            return;
        }

        formRef.current.setFieldError("password", errorMessage);
        formRef.current.setFieldError("repeatedPassword", errorMessage);
        formRef.current.setFieldTouched("password", true);
        formRef.current.setFieldTouched("repeatedPassword", true);
    }, [errorMessage]);

    const firstValidation = {
        description: minLengthValidationMsg,
        status: (password) => {
            if (!password) {
                return "INITIAL";
            }
            return password?.length >= minLength && password?.length <= maxLength ? "DONE" : "WRONG";
        },
    };

    const validations = [
        {
            description: minLengthValidationMsg,
            status: (password) => {
                if (!password) {
                    return "INITIAL";
                }
                return password?.length >= minLength && password?.length <= maxLength ? "DONE" : "WRONG";
            },
        },
        {
            description: i18nUtils.get("onboarding.password.validation2", "Mínimo 1 minúscula."),
            status: (password) => {
                if (!password) {
                    return "INITIAL";
                }
                return /[a-z]/.test(password) ? "DONE" : "WRONG";
            },
        },
        {
            description: i18nUtils.get("onboarding.password.validation1", "Mínimo 1 mayúscula."),
            status: (password) => {
                if (!password) {
                    return "INITIAL";
                }
                return /[A-Z]/.test(password) ? "DONE" : "WRONG";
            },
        },
        {
            description: i18nUtils.get("onboarding.password.validation3", "Mínimo 1 número."),
            status: (password) => {
                if (!password) {
                    return "INITIAL";
                }
                return /\d/.test(password) ? "DONE" : "WRONG";
            },
        },
        {
            description: i18nUtils.get(
                "onboarding.password.validation4",
                "Mínimo 1 de los siguientes símbolos @$#!%&()_+{}?*",
            ),
            status: (password) => {
                if (!password) {
                    return "INITIAL";
                }
                const symbols = /[@$#¡!%&()_{}?¿*]/;
                return symbols.test(password) ? "DONE" : "WRONG";
            },
        },
    ];

    const notContainValidations = [
        {
            description: i18nUtils.get("onboarding.password.validation5", "Más de 2 caracteres repetidos. Ej: AA"),
            status: (password) => {
                if (!password) {
                    return "INITIAL";
                }
                return /([a-zA-Z])\1/.test(password) ? "WRONG" : "DONE";
            },
        },
        {
            description: i18nUtils.get(
                "onboarding.password.validation6",
                "Más de 3 caracteres consecutivos. Ej: 1234 o abcd",
            ),
            status: (password) => {
                if (!password) {
                    return "INITIAL";
                }
                return /(0123|1234|2345|3456|4567|5678|6789|abcd|bcde|cdef|defg|efgh|fghi|ghij|hijk|ijkl|jklm|klmn|lmnñ|mnño|nñop|ñopq|opqr|pqrs|qrst|rstu|stuv|tuvw|uvwx|vwxy|wxyz)+/.test(
                    password,
                )
                    ? "WRONG"
                    : "DONE";
            },
        },
    ];

    const getItems = (password) =>
        validations.map((val) => ({ status: val.status(password), description: val.description }));

    const getNotContainItems = (password) =>
        notContainValidations.map((val) => ({
            status: val.status(password),
            description: val.description,
        }));

    const passwordStrength = (password) => {
        const numInvalidValidations = validations.reduce(
            (invalids, val) => (val.status(password) !== "DONE" ? invalids + 1 : invalids),
            0,
        );
        const numInvalidNotContainValidations = notContainValidations.reduce(
            (invalids, val) => (val.status(password) !== "DONE" ? invalids + 1 : invalids),
            0,
        );

        const totalNumInvalidValidations = numInvalidValidations + numInvalidNotContainValidations;

        if (totalNumInvalidValidations === 0) {
            return 67;
        }

        if (totalNumInvalidValidations <= 1) {
            return 34;
        }

        if (password) {
            return 1;
        }

        return -1;
    };

    const submitIsDisabled = ({ password, repeatedPassword }) => {
        const firstValidationWrong = firstValidation.status(password) !== "DONE";

        const numInvalidValidations = validations.reduce(
            (invalids, val) => (val.status(password) !== "DONE" ? invalids + 1 : invalids),
            0,
        );
        const numInvalidNotContainValidations = notContainValidations.reduce(
            (invalids, val) => (val.status(password) !== "DONE" ? invalids + 1 : invalids),
            0,
        );

        const totalNumInvalidValidations = numInvalidValidations + numInvalidNotContainValidations;
        const repeatedPasswordIsDifferent = repeatedPassword !== password;

        return firstValidationWrong || totalNumInvalidValidations > 0 || repeatedPasswordIsDifferent;
    };

    const onHeaderBack = () => {
        dispatch(goBack());
    };

    const formikProps = {
        validateOnChange: true,
        validateOnBlur: false,
        ref: formRef,
        initialValues: {
            password: _password || "",
            repeatedPassword: passwordConfirmation || "",
        },
        validationSchema: Yup.lazy((values) =>
            Yup.object().shape({
                password: Yup.string()
                    .required(i18nUtils.get("enrollment.step3.password.error.empty"))
                    .min(minLength, minLengthValidationMsg)
                    .max(maxLength, maxLengthValidationMsg)
                    .matches(
                        new RegExp(configUtils.get("core.userconfiguration.password.pattern"), "g"),
                        i18nUtils.get("enrollment.step3.password.error.unfulfilledRules"),
                    )
                    .oneOf(
                        [values.repeatedPassword],
                        i18nUtils.get("enrollment.step3.password.error.passwordConfirmationMustMatch"),
                    ),
                repeatedPassword: Yup.string()
                    .matches(
                        new RegExp(configUtils.get("core.userconfiguration.password.pattern"), "g"),
                        i18nUtils.get("enrollment.step3.password.error.unfulfilledRules"),
                    )
                    .oneOf(
                        [values.password],
                        i18nUtils.get("enrollment.step3.password.error.passwordConfirmationMustMatch"),
                    ),
            }),
        ),
        onSubmit: ({ password, repeatedPassword }) => {
            if (password && repeatedPassword) {
                if (onboardingWithInvitation === null) {
                    gaEventTracker.triggerEvent(events.registry8);
                }
                dispatch(onboardingActions.setPassword(password, repeatedPassword));
                dispatch(push("/onboarding/stepSelectSecuritySeal"));
            }
        },
    };

    return (
        <>
            {" "}
            {isMobile && (
                <ResponsiveHeader id="onboarding.noUser.title" onBack={isMobile && onHeaderBack} showCenterContent />
            )}
            <MainContainer>
                {!isMobile && <Head title="onboarding.noUser.title" onBack={isMobile && onHeaderBack} />}
                <Stepper isMobile={isMobile} page={PAGE_ID} />
                <h1 className="text-center" style={{ textAlign: "left" }}>
                    <CustomText text={i18nUtils.get("", "Elegí una contraseña")} />
                </h1>
                <Formik {...formikProps}>
                    {(allFormik) => (
                        <Form className="above-the-fold">
                            <Field
                                component={Credential}
                                idForm={FORM_ID}
                                name="password"
                                maxLength={maxLength}
                                showStrength
                                strength={passwordStrength(allFormik?.values?.password)}
                                labelTextDefault={i18nUtils.get("onboarding.password.label", "Contraseña")}
                                placeholder={i18nUtils.get("onboarding.password.placeholder", "Ej: Contraseña22@")}
                                autoFocus={!isMobile}
                            />

                            <ToDoList items={getItems(allFormik?.values?.password)} />
                            <I18n id="onboarding.password.notContainValidations" defaultValue="No debe contener:" />
                            <ToDoList items={getNotContainItems(allFormik?.values?.password)} />

                            <Field
                                component={Credential}
                                idForm={FORM_ID}
                                name="repeatedPassword"
                                labelTextDefault={i18nUtils.get(
                                    "onboarding.repeatedPassword.label",
                                    "Repetir contraseña",
                                )}
                                placeholder={i18nUtils.get(
                                    "onboarding.repeatedPassword.placeholder",
                                    "Ej: Contraseña22@",
                                )}
                            />
                            <div className="form-actions">
                                <Button
                                    bsStyle="primary"
                                    label="global.continue"
                                    loading={false}
                                    type="submit"
                                    block
                                    disabled={submitIsDisabled(allFormik?.values)}
                                />
                            </div>
                        </Form>
                    )}
                </Formik>
            </MainContainer>
        </>
    );
};

StepPassword.propTypes = {
    isMobile: bool.isRequired,
    onboardingWithInvitation: bool.isRequired,
};

const mapStateToProps = (state) => ({
    onboardingWithInvitation: onboardingSelectors.getOnboardingWithInvitation(state),
});

export default connect(mapStateToProps)(StepPassword);
